import { useState, useEffect, useRef } from 'react';
import useNavigateWithSearch from '../../../../hooks/useNavigateWithSearch';
import { PerksStep, CommentsStep, ConfirmStep, FinalStep } from './Steps';
import useSubscriptionStatus from '../../../../hooks/useSubscriptionStatus';
import PageContent from '../../global/PageContent';
import { ROUTE_MANAGE_MEMBERSHIP } from '../../config/routes';
import LoadingBar from '../../global/LoadingBar';
import useScrollToTop from '../../utils/hooks/useScrollToTop';
import { useFirebaseAuth } from '../../../global/FirebaseProvider/FirebaseProvider';
import { pushCancellationStartedEvent, pushSubscriptionCancelledEvent } from '../../utils/gtm';

import './CancelSubscription.scss';

const CANCEL_SUBSCRIPTION_STEPS = {
  PERKS: 'perks',
  COMMENTS: 'comments',
  CONFIRM: 'confirm',
  FINAL: 'final',
};

const CancelSubscription = () => {
  useScrollToTop();
  const { userData } = useFirebaseAuth();
  const {
    membershipSubscriptionData,
    setMembershipSubscriptionData,
    isLoading: isLoadingSubscription } = useSubscriptionStatus();
  const navigateWithSearch = useNavigateWithSearch();
  const [step, setStep] = useState(CANCEL_SUBSCRIPTION_STEPS.PERKS);
  const [comments, setComments] = useState('');
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const cancellationStartedRef = useRef(false);

  useEffect(() => {
    if (userData && !cancellationStartedRef.current) {
      cancellationStartedRef.current = true;
      pushCancellationStartedEvent(userData);
    }
  }, [userData]);

  useEffect(() => {
    if (
      membershipSubscriptionData?.cancelAtPeriodEnd === true &&
      CANCEL_SUBSCRIPTION_STEPS.PERKS === step) {
      navigateWithSearch(ROUTE_MANAGE_MEMBERSHIP);
    }
  }, [membershipSubscriptionData, navigateWithSearch, step]);

  return isLoadingSubscription ? (
    <LoadingBar />
  ) : (
    <PageContent
      isLoading={isLoadingSubscription || isLoading}
      error={error}
      className="CancelSubscription"
      backButtonText="GO TO MANAGE MEMBERSHIP"
      parentUrl={ROUTE_MANAGE_MEMBERSHIP}
    >
      <div className="CancelSubscription__container">
        {step === CANCEL_SUBSCRIPTION_STEPS.PERKS && (
          <PerksStep
            onHandleNext={() => setStep(CANCEL_SUBSCRIPTION_STEPS.COMMENTS)}
            setIsLoading={setIsLoading}
          />
        )}
        {step === CANCEL_SUBSCRIPTION_STEPS.COMMENTS && (
          <CommentsStep
            onHandleNext={() => setStep(CANCEL_SUBSCRIPTION_STEPS.CONFIRM)}
            setComments={setComments}
            comments={comments}
            setReason={setReason}
            reason={reason}
          />
        )}
        {step === CANCEL_SUBSCRIPTION_STEPS.CONFIRM && (
          <ConfirmStep
            onHandleNext={() => {
              pushSubscriptionCancelledEvent({
                userData,
                cancelReason: comments?.description,
                cancelComment: reason,
              });
              setStep(CANCEL_SUBSCRIPTION_STEPS.FINAL);
            }}
            membershipSubscriptionData={membershipSubscriptionData}
            setMembershipSubscriptionData={setMembershipSubscriptionData}
            setIsLoading={setIsLoading}
            comments={comments}
            reason={reason}
            setError={setError}
          />
        )}
        {step === CANCEL_SUBSCRIPTION_STEPS.FINAL && (
          <FinalStep membershipSubscriptionData={membershipSubscriptionData} />
        )}
      </div>
    </PageContent>
  );
};

export default CancelSubscription;
